import React, { useState } from 'react';

import { Button, FocusRing, Text } from '@change/ds-core-ui';
import { TextArea, TextField } from '@change/ds-forms';
import { ThumbDownIcon, ThumbUpIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

export type NotHelpfulReason = 'inaccurate' | 'missing_context' | 'too_long' | 'prefer_no_ai';

const REASONS: NotHelpfulReason[] = ['inaccurate', 'missing_context', 'too_long', 'prefer_no_ai'];

type Props = Readonly<{
	onFeedbackYes: () => void;
	onFeedbackNoClick: () => void;
	onFeedbackNo: (reasons: NotHelpfulReason[], details: string) => void;
	onFeedbackCancelled: () => void;
	onReasonSelected: (reason: NotHelpfulReason) => void;
	/** Direction stored from a prior session ('yes'/'no'); disables both buttons and shows the correct selected style on reload */
	storedFeedbackDirection?: 'yes' | 'no' | null;
}>;

// oxlint-disable-next-line max-lines-per-function, complexity
export const AiSummaryFeedbackLinksStaggered: React.FC<Props> = ({
	onFeedbackYes,
	onFeedbackNoClick,
	onFeedbackNo,
	onFeedbackCancelled,
	onReasonSelected,
	storedFeedbackDirection = null,
}) => {
	const { translate } = useI18n();
	const [showForm, setShowForm] = useState(false);
	const [thumbsUpSelected, setThumbsUpSelected] = useState(false);
	const [thumbsDownSubmitted, setThumbsDownSubmitted] = useState(false);
	const [selectedReasons, setSelectedReasons] = useState<NotHelpfulReason[]>([]);
	const [details, setDetails] = useState('');

	const toggleReason = (reason: NotHelpfulReason) => {
		setSelectedReasons((prev) => {
			if (prev.includes(reason)) {
				return prev.filter((r) => r !== reason);
			}
			onReasonSelected(reason);
			return [...prev, reason];
		});
	};

	const handleThumbsUp = () => {
		setThumbsUpSelected(true);
		onFeedbackYes();
	};

	const handleThumbsDown = () => {
		setShowForm(true);
		onFeedbackNoClick();
	};

	const handleCancel = () => {
		setShowForm(false);
		// Reset form state so stale selections don't leak into a later submission
		setSelectedReasons([]);
		setDetails('');
		onFeedbackCancelled();
	};

	const handleSubmit = () => {
		setShowForm(false);
		setThumbsDownSubmitted(true);
		onFeedbackNo(selectedReasons, details);
	};

	// Base class without opacity — opacity-40 applied conditionally per button
	// so the selected button keeps full color while the other one fades
	const iconButtonBase =
		'flex items-center justify-center rounded-full p-1.5 transition-colors enabled:hover:bg-gray-100 disabled:cursor-default';

	return (
		<div className="relative">
			<div className="flex items-center gap-1">
				<FocusRing focusRingClass="rounded-full">
					<button
						type="button"
						data-qa="ai-summary-feedback-yes"
						onClick={handleThumbsUp}
						disabled={storedFeedbackDirection !== null || showForm || thumbsUpSelected || thumbsDownSubmitted}
						className={cn(
							iconButtonBase,
							// Faded when the other option is active/selected
							(storedFeedbackDirection === 'no' || (!storedFeedbackDirection && (showForm || thumbsDownSubmitted))) &&
								'opacity-40',
							storedFeedbackDirection === 'yes' || thumbsUpSelected ? 'text-icon-selected' : 'text-icon-weak',
						)}
						aria-label={translate('corgi.petition_details.ai_summary.feedback_yes')}
						aria-pressed={thumbsUpSelected || storedFeedbackDirection === 'yes'}
					>
						<ThumbUpIcon size="small" />
					</button>
				</FocusRing>
				<FocusRing focusRingClass="rounded-full">
					<button
						type="button"
						data-qa="ai-summary-feedback-no"
						onClick={handleThumbsDown}
						disabled={storedFeedbackDirection !== null || thumbsUpSelected || thumbsDownSubmitted}
						aria-expanded={showForm}
						aria-pressed={showForm || thumbsDownSubmitted || storedFeedbackDirection === 'no'}
						className={cn(
							iconButtonBase,
							// Faded when the other option is active/selected
							(storedFeedbackDirection === 'yes' || (!storedFeedbackDirection && thumbsUpSelected)) && 'opacity-40',
							storedFeedbackDirection === 'no' || showForm || thumbsDownSubmitted
								? 'text-icon-selected'
								: 'text-icon-weak',
						)}
						aria-label={translate('corgi.petition_details.ai_summary.feedback_no')}
					>
						<ThumbDownIcon size="small" />
					</button>
				</FocusRing>
			</div>
			{showForm && (
				<div className="border-stroke-weaker absolute top-full right-0 z-50 mt-2 w-80 rounded-xl border bg-white p-4 shadow-lg">
					<div className="flex flex-col gap-4">
						<div>
							<Text as="p" className="text-typography-strong" fontWeight="bold" size="medium" variant="body">
								<Translate value="corgi.petition_details.ai_summary.not_helpful_modal_title" />
							</Text>
							<Text as="p" className="text-typography-weak mt-1" size="small" variant="body">
								<Translate value="corgi.petition_details.ai_summary.not_helpful_modal_subtitle" />
							</Text>
						</div>
						<div
							className="flex flex-wrap gap-2"
							// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
							role="group"
							aria-label={translate('corgi.petition_details.ai_summary.not_helpful_modal_title')}
						>
							{REASONS.map((reason) => (
								<FocusRing key={reason} focusRingClass="rounded-full">
									<button
										type="button"
										onClick={() => toggleReason(reason)}
										aria-pressed={selectedReasons.includes(reason)}
										className={cn(
											'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
											selectedReasons.includes(reason)
												? 'bg-fill-strong text-typography-inverse-strong border-transparent'
												: 'border-stroke-weak text-typography-strong-fixed hover:border-stroke-hover bg-white',
										)}
									>
										<Translate value={`corgi.petition_details.ai_summary.not_helpful_reason_${reason}`} />
									</button>
								</FocusRing>
							))}
						</div>
						<TextField
							input={<TextArea className="resize-none" />}
							id="ai-summary-feedback-details"
							name="ai-summary-feedback-details"
							value={details}
							onChange={(value) => setDetails(value)}
							placeholder={translate('corgi.petition_details.ai_summary.not_helpful_details_placeholder')}
							aria-label={translate('corgi.petition_details.ai_summary.not_helpful_details_placeholder')}
							label={translate('corgi.petition_details.ai_summary.not_helpful_details_placeholder')}
						/>
						<div className="flex items-center justify-end gap-4">
							<Button type="button" variant="tertiary" data-testid="ai-summary-cancel-feedback" onClick={handleCancel}>
								<Translate value="corgi.petition_details.ai_summary.not_helpful_cancel" />
							</Button>
							<Button type="button" variant="primary" data-testid="ai-summary-send-feedback" onClick={handleSubmit}>
								<Translate value="corgi.petition_details.ai_summary.not_helpful_submit" />
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
